/**
 * The Market screen route (`/market`, docs/market-ux.md): a destination reached from the game
 * page's market names and lines (and "View market" in Portfolio, Market making and the ticket),
 * not a nav section. The screen is components/market/MarketScreen.tsx; this file wires its
 * `MarketSource` to the real hooks and mirrors the market on screen into the URL (`?c=<contestId>&m=<marketKey>&b=<bookKey>`, or a
 * `&h=<positionHash>` link that resolves to its market and line).
 */
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import type { LiquidityPosition, Opportunity } from "@greenbook/client";
import {
  invalidateAfterOrder,
  useAccountPartners,
  useCancelOrders,
  useContestState,
  useDepth,
  useOrders,
  usePartners,
  usePriceHistory,
} from "../lib/queries.js";
import { useAccess, useIsAuthed } from "../lib/omauth.js";
import { useTradingMode } from "../lib/trading.js";
import { showUnlock } from "../components/Gate.js";
import { TradeTicket } from "../components/TradeTicket.js";
import { marketHref, parseMarketParams, RANGES, type MarketTarget } from "../lib/marketView.js";
import {
  buildOppBook,
  getOut,
  ifAllFill,
  kpiState,
  settledResult,
  useMarketMakingAccess,
  type MmLeg,
} from "../lib/marketMaking.js";
import { liveSource as mm } from "./MarketMaking.js";
import { errorText, modeOf } from "../components/marketMaking/shared.js";
import { MarketScreen } from "../components/market/MarketScreen.js";
import type { MarketSource, MmQuoteView } from "../components/market/source.js";

/* ============================================================== live source */

const DEPTH_REFETCH_MS = 10_000;

export const liveMarketSource: MarketSource = {
  useContest: mm.useContest,
  useGameBook: mm.useGameBook,
  useScope() {
    const mode = useTradingMode();
    const authed = useIsAuthed();
    const ap = useAccountPartners();
    const catalog = usePartners();
    return useMemo(() => {
      const connected = authed && ap.data ? new Set((ap.data.account_partners ?? []).filter((p) => p.status === "active" && p.credential_status === "connected").map((p) => p.partner_id)) : null;
      // A venue takes limits unless its order_config says otherwise (unknown → null).
      const cat = catalog.data
        ? new Map(
            catalog.data.map((p) => {
              const t = p.order_config?.supported_order_types;
              return [p.partner_id, { limit: Array.isArray(t) ? t.includes("limit") : null }] as const;
            }),
          )
        : null;
      return { mode, allowed: mode === "real" ? (connected ?? new Set<string>()) : null, connected, catalog: cat };
    }, [mode, authed, ap.data, catalog.data]);
  },
  useAccess(scope) {
    return useAccess(scope);
  },
  unlock(scope) {
    showUnlock(scope);
  },
  useDepth(hash) {
    const q = useDepth(hash, { refetchMs: DEPTH_REFETCH_MS });
    return { data: q.data, isLoading: !!hash && q.isLoading, isError: q.isError, error: q.error, refetch: () => void q.refetch() };
  },
  usePriceHistory(contestId, hashes, range) {
    const r = RANGES.find((x) => x.key === range) ?? RANGES[1];
    const q = usePriceHistory(contestId, { position_hash: hashes.join(","), windowMs: r.ms, resolution: r.resolution, limit: r.limit });
    return { points: q.data?.points, isLoading: q.isLoading, isError: q.isError, error: q.error, refetch: () => void q.refetch() };
  },
  useContestState(id) {
    return useContestState(id).data;
  },
  useMyOrders(ccy) {
    const q = useOrders(200, { status: "open", currency: ccy });
    const items = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.data), [q.data]);
    return { items, isLoading: q.isLoading };
  },
  useCancel() {
    const m = useCancelOrders();
    const qc = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const cancel = useCallback(
      async (ids: string[]) => {
        setError(null);
        try {
          const res = await m.mutateAsync(ids);
          const errs = res.data.errors ?? [];
          if (errs.length) setError(errs.map((e) => e.error).join(" · "));
        } catch (e) {
          setError(errorText(e));
        } finally {
          invalidateAfterOrder(qc);
        }
      },
      [m, qc],
    );
    return { cancel, busy: m.isPending, error };
  },
  useMmQuote(contestId, marketKey, bookKey, positions) {
    const registry = mm.useRegistry();
    const entry = registry.find((e) => e.contestId === contestId && e.marketKey === marketKey && e.bookKey === bookKey) ?? null;
    const ids = useMemo(() => (entry ? [entry.opportunityId] : []), [entry]);
    const oppOf = mm.useOpportunities(ids);
    const scope = mm.useScope(entry ? modeOf(entry.ccy) : "paper");
    const st = entry ? oppOf(entry.opportunityId) : null;
    return useMemo(() => (entry && st?.data ? mmQuoteView(entry, st.data, positions, scope.allowed) : null), [entry, st?.data, positions, scope.allowed]);
  },
  useCanMakeMarkets() {
    return useMarketMakingAccess();
  },
  renderTicket({ position, pl, maxPrice, onClose }) {
    return <TradeTicket position={position} pl={pl} initialMaxPrice={maxPrice} marketLink={false} onClose={onClose} />;
  },
};

/** The card's big number, the same way the Market making card works it out. */
export function mmQuoteView(
  entry: MmQuoteView["entry"],
  opp: Opportunity,
  positions: (LiquidityPosition | undefined)[],
  allowed: ReadonlySet<string> | null,
): MmQuoteView {
  const book = buildOppBook(entry.sides, (opp.legs ?? []) as MmLeg[], entry.legs);
  const state = kpiState(book);
  const live = entry.sides.map((s) => positions.find((p) => p?.position_hash === s.positionHash)) as [LiquidityPosition | undefined, LiquidityPosition | undefined];
  const out = state === "exposed" ? getOut(book, live, allowed, { ccy: entry.ccy, fees: entry.ccy === "USD" }) : null;
  const value =
    state === "working" ? Math.min(...ifAllFill(book)) : state === "exposed" ? (out?.locked ?? null) : state === "locked" ? Math.min(...book.payout) : settledResult(book);
  const caption = state === "working" ? "if both fill" : state === "exposed" ? (out ? "to get out now" : "no price to get out") : state === "locked" ? "locked" : "final";
  return { entry, state, value, caption };
}

/* ==================================================================== route */

export function Market() {
  const [params, setParams] = useSearchParams();
  const target = useMemo(() => parseMarketParams(params), [params]);
  const onTarget = useCallback(
    (t: MarketTarget | null) => {
      const href = t ? marketHref(t) : "/market";
      const qs = href.includes("?") ? href.slice(href.indexOf("?") + 1) : "";
      if (qs !== params.toString()) setParams(new URLSearchParams(qs), { replace: true });
    },
    [params, setParams],
  );
  return <MarketScreen source={liveMarketSource} target={target} onTarget={onTarget} />;
}
